import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from '../Image';
import { answersTransformer } from '../../transformer';
import Pdf from '../../service/Pdf';

import styles from './styles.module.scss';

const Result = ({
  stages,
  setStage,
  recommendation,
  setRecommendation,
  downloadOnly,
}) => {
  const { t } = useTranslation();
  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const [mktoForms2Success, setMktoForms2Success] = useState(true === downloadOnly ?? false);
  const hasLoaded = useRef(false);
  const mktoFormRef = useRef(null);
  const pdf = new Pdf();

  const changeToDevices = () => {
    const stage = stages[1];
    stage.curStep = 1;

    setStage(stage);
    setRecommendation('devices');
  };

  const changeToSolutions = () => {
    const stage = stages[0];
    stage.curStep = 1;

    setStage(stage);
    setRecommendation('solutions');
  };

  const getAnswers = () => {
    return answersTransformer(stages, recommendation);
  };

  const downloadPdf = () => {
    setDownloadDisabled(true);

    const answers = getAnswers();

    pdf.setAnswers(answers);
    pdf.setTranslation(t);

    pdf
      .process(answers)
      .then((output) => {
        pdf.save(output);
        setDownloadDisabled(false);
      })
      .catch((e) => {
        console.log('error', e);
        setDownloadDisabled(false);
      });

    // Adobe tracking
    if (window.globalLmd) {
      window.lmd = window.globalLmd;
      window.lmd.download = t(`tool_ui.thankyou.download`);
      window._satellite.track('Download');
    }
  };

  const getInTouch = () => {
    // Request contact action from parent page
    window.parent.postMessage(
      {
        name: 'contact-rep',
        fields: [{
          field: 'webformReferralPageURLLast',
          value: (window.location !== window.parent.location) ? document.referrer : document.location.href
        }, {
          field: t('tool_ui.gating_functionality.link_field_name'),
          value: stages.slice(-1)[0]?.link
        }]
      },
      '*'
    );

    // Adobe tracking
    if (window.globalLmd) {
      window.lmd = window.globalLmd;
      window.lmd.formType = t(`tool_ui.thankyou.get_in_touch`);
      window._satellite.track('Form_Request');
    }
  };

  useEffect(() => {
    if (typeof window.MktoForms2 !== 'undefined') {
      window.MktoForms2.whenReady((form) => {
        const extraFields = {};
        extraFields[t('tool_ui.gating_functionality.link_field_name')] = stages.slice(-1)[0]?.link;
        extraFields['webformReferralPageURLLast'] = (window.location !== window.parent.location) ? document.referrer : document.location.href;

        form.addHiddenFields(extraFields);

        form.onSuccess(() => {
          setMktoForms2Success(true);
          return false;
        });
      });
    }
  }, [stages, t]);

  useEffect(() => {
    const forstaTrackingUrl = process.env.REACT_APP_FORSTA_TRACKING_URL;
    if (undefined === forstaTrackingUrl) {
      return;
    }

    const forstaTrackingScriptExists = document.querySelector(
      'script[src="' + forstaTrackingUrl + '"]'
    );
    if (null !== forstaTrackingScriptExists) {
      return;
    }

    // Add Forsta Tracking
    const forstaTrackingScript = document.createElement('script');
    forstaTrackingScript.src = forstaTrackingUrl;
    document.body.appendChild(forstaTrackingScript);
  }, []);

  useEffect(() => {
    if (
      !hasLoaded.current &&
      !mktoForms2Success &&
      t('tool_ui.gating_functionality.gating_required') !==
        'tool_ui.gating_functionality.gating_required' &&
      t('tool_ui.gating_functionality.gating_required') === 'yes'
    ) {
      window.PDFlink = stages.slice(-1)[0]?.link;
      //Add MktoForms2
      const mktoForms2Script = document.createElement('script');
      mktoForms2Script.innerHTML = `MktoForms2.loadForm("//pages.lenovo.com", "183-WCT-620", ${t(
        'tool_ui.gating_functionality.marketo_formid'
      )})`;
      document.body.appendChild(mktoForms2Script);
      hasLoaded.current = true;
    }
  }, [t, mktoForms2Success, stages]);

  useEffect(() => {
    if (mktoForms2Success && !downloadOnly) {
      // Adobe tracking
      if (window.globalLmd) {
        window.lmd = window.globalLmd;
        window.lmd.formType = 'DWS-Gated';
        window._satellite.track('Form_Submit');
      }

      downloadPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mktoForms2Success, downloadOnly]);

  const isMktoFormRequired = t('tool_ui.gating_functionality.gating_required') === 'yes';
  let pageTitle = t('tool_ui.thankyou.title');
  let pageSubTitle = t('tool_ui.thankyou.subtitle');
  let pageBody = t('tool_ui.thankyou.body');
  if (isMktoFormRequired && !downloadOnly) {
    if (mktoForms2Success) {
      pageTitle = t('tool_ui.thankyou_gated.title_2');
      pageSubTitle = t('tool_ui.thankyou_gated.subtitle_2');
      pageBody = t('tool_ui.thankyou_gated.body_2');
    } else {
      pageBody = t('tool_ui.thankyou_gated.body_1');
    }
  }

  useEffect(() => {
    if (null === mktoFormRef.current) return;

    const updateCheckboxes = () => {
      const checkboxes =
        mktoFormRef.current.querySelectorAll('.mktoCheckboxList');
      checkboxes.forEach((checkboxElm) => {
        checkboxElm
          .closest('.mktoFieldWrap')
          .classList.add('mktoFieldCheckboxWrap');
      });
    };

    const checkMktoHasLoaded = () => {
      if (0 < mktoFormRef.current.children.length) {
        // Adobe tracking
        if (window.globalLmd) {
          window.lmd = window.globalLmd;
          window.lmd.formType = 'DWS-Gated';
          window._satellite.track('Form_Request');
        }

        updateCheckboxes();
      } else {
        setTimeout(() => {
          checkMktoHasLoaded();
        }, 100);
      }
    };

    checkMktoHasLoaded();
  }, []);

  return (
    <section className={styles.component}>
      <div className={styles.thank_you}>
        <h2 className={styles.title}>
          {!isMktoFormRequired ? (
            <Image
              src={'images_iax/icon-winner'}
              type={'svg'}
              className={styles.icon}
            />
          ) : null}
          {pageTitle}
        </h2>
        <p className={styles.sub_title}>{pageSubTitle}</p>
        <p className={styles.text}>{pageBody}</p>

        {isMktoFormRequired && !mktoForms2Success ? (
          <form
            ref={mktoFormRef}
            id={`mktoForm_${t('tool_ui.gating_functionality.marketo_formid')}`}
            className={styles.mkto_form}
          ></form>
        ) : null}

        {!isMktoFormRequired ? (
          <div className={styles.buttons}>
            <button
              onClick={downloadPdf}
              disabled={downloadDisabled}
              className={downloadDisabled ? styles.generating : ''}
            >
              {downloadDisabled ? t(`tool_ui.global.generating_pdf`) : t(`tool_ui.thankyou.download`)}
            </button>
            {!isMktoFormRequired ? <button onClick={getInTouch} className={styles.get_in_touch}>
              {t(`tool_ui.thankyou.get_in_touch`)}
            </button> : ''}
          </div>
        ) : null}

        {'both' === recommendation && (isMktoFormRequired && (mktoForms2Success || downloadOnly)) ? <div className={styles.buttons}>
          <button
            onClick={downloadPdf}
            disabled={downloadDisabled}
            className={downloadDisabled ? styles.generating : ''}
          >
            {downloadDisabled ? t(`tool_ui.global.generating_pdf`) : t(`tool_ui.thankyou.download`)}
          </button>
          <a
            href={t(`tool_ui.thankyou_gated.cta_both_option_url`)}
            className={styles.get_in_touch}
            rel='noreferrer'
            target={'_blank'}
          >
            {t(`tool_ui.thankyou_gated.cta_both_option`)}
          </a>
        </div> : isMktoFormRequired && mktoForms2Success ? <div className={styles.buttons}>
          <button
            onClick={downloadPdf}
            disabled={downloadDisabled}
            className={downloadDisabled ? styles.generating : ''}
          >
            {downloadDisabled ? t(`tool_ui.global.generating_pdf`) : t(`tool_ui.thankyou.download`)}
          </button>
        </div> : ''}
        <div className={'forsta_tracking_holder'}></div>
      </div>

      {'solutions' === recommendation && (mktoForms2Success || !isMktoFormRequired) ? (
        <div className={styles.take_me_to}>
          <h2>{t(`tool_ui.thankyou.cp_title_sl`)}</h2>
          <p>{t(`tool_ui.thankyou.cp_body`)}</p>
          <button onClick={changeToDevices}>
            <Image
              src={'images_iax/icon-devices'}
              type={'svg'}
              className={styles.icon}
            />
            {t(`tool_ui.thankyou.cp_cta_sl`)}
          </button>
        </div>
      ) : null}

      {'devices' === recommendation && (mktoForms2Success || !isMktoFormRequired) ? (
        <div className={styles.take_me_to}>
          <h2>{t(`tool_ui.thankyou.cp_title_dev`)}</h2>
          <p>{t(`tool_ui.thankyou.cp_body`)}</p>
          <button onClick={changeToSolutions}>
            <Image
              src={'images_iax/icon-solutions'}
              type={'svg'}
              className={styles.icon}
            />
            {t(`tool_ui.thankyou.cp_cta_dev`)}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default Result;
